import { useState, type FormEvent } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requestPasswordReset } from '@/lib/auth';

/**
 * パスワードリセット申請 (`/auth/forgot-password`)。
 *
 * メールアドレスを送信すると Supabase がリセットリンク付きメールを送る
 * (実際に届くかは Supabase プロジェクトのメール配信設定に依存)。列挙攻撃対策として
 * メールの存在有無に関わらず同じ成功メッセージを表示する。
 */
function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      console.error('[auth] requestPasswordReset failed', err);
      setError('通信エラーが発生しました。接続を確認して再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>パスワードをリセット</CardTitle>
          <CardDescription>
            登録済みのメールアドレスにリセット用リンクを送ります。
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {sent ? (
            <p className="text-sm text-muted-foreground">
              メールを送信しました。届いたメールのリンクから新しいパスワードを設定してください。
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email">メール</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <Button type="submit" disabled={submitting}>
                {submitting ? '送信中…' : 'リセットリンクを送る'}
              </Button>
            </form>
          )}
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/signin"
              className="text-foreground underline underline-offset-2 hover:opacity-80"
            >
              サインインに戻る
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/auth/forgot-password')({
  component: ForgotPasswordPage,
});
